import { useEffect, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Grid, Chip, CircularProgress, Tooltip } from '@material-ui/core';
import { Page, Header, Content, Link } from '@backstage/core-components';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { GitlabCIApiRef } from '@immobiliarelabs/backstage-plugin-gitlab';
import { configApiRef } from '@backstage/core-plugin-api';

const FONT = "'Arial', sans-serif";

const useStyles = makeStyles(theme => ({
  layout: {
    display: 'flex',
    flexDirection: 'row' as const,
    alignItems: 'flex-start',
    gap: 0,
    minHeight: '100%',
  },
  mainContent: {
    flex: 1,
    minWidth: 0,
  },
  divider: {
    width: '1px',
    alignSelf: 'stretch',
    background: theme.palette.divider,
    margin: '0 0 0 24px',
    flexShrink: 0,
  },
  sidePanel: {
    width: '280px',
    flexShrink: 0,
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '32px',
    padding: '0 0 0 32px',
  },

  /* ---- section labels ---- */
  sectionLabel: {
    fontFamily: FONT,
    fontWeight: 700,
    fontSize: '12px',
    lineHeight: '16px',
    color: theme.palette.text.secondary,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
    margin: '0 0 8px 0',
  },

  /* ---- summary cards (left) ---- */
  summaryCard: {
    display: 'flex',
    flexDirection: 'column' as const,
    padding: '16px',
    background: theme.palette.background.paper,
    borderRadius: '4px',
    boxShadow: '0px 1px 3px rgba(0,0,0,0.08)',
    gap: '6px',
    height: '100%',
    boxSizing: 'border-box' as const,
  },
  summaryCardLabel: {
    fontFamily: FONT,
    fontWeight: 400,
    fontSize: '14px',
    lineHeight: '20px',
    color: theme.palette.text.secondary,
    margin: 0,
  },
  summaryCardValue: {
    fontFamily: FONT,
    fontWeight: 700,
    fontSize: '40px',
    lineHeight: '52px',
    letterSpacing: '-0.5px',
    color: '#007EB1',
    margin: 0,
  },

  /* ---- recent items (left) ---- */
  entityCard: {
    display: 'flex',
    flexDirection: 'column' as const,
    padding: '16px',
    gap: '4px',
    background: theme.palette.background.paper,
    borderRadius: '4px',
    boxShadow: '0px 1px 3px rgba(0,0,0,0.08)',
    borderLeft: `3px solid ${theme.palette.primary.main}`,
  },
  entityCardRow: {
    display: 'flex',
    flexDirection: 'row' as const,
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '8px',
  },
  entityCardTitle: {
    fontFamily: FONT,
    fontWeight: 700,
    fontSize: '14px',
    lineHeight: '20px',
    color: theme.palette.text.primary,
    margin: 0,
  },
  entityCardSub: {
    fontFamily: FONT,
    fontWeight: 400,
    fontSize: '12px',
    lineHeight: '16px',
    color: theme.palette.text.secondary,
    margin: 0,
  },
  chip: {
    height: '20px',
    fontSize: '11px',
    fontFamily: FONT,
    fontWeight: 600,
    borderRadius: '10px',
  },
  browseLink: {
    fontFamily: FONT,
    fontWeight: 600,
    fontSize: '14px',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
  },

  /* ---- side panel ---- */
  sidePanelLabel: {
    fontFamily: FONT,
    fontWeight: 700,
    fontSize: '12px',
    lineHeight: '16px',
    color: theme.palette.text.secondary,
    margin: '0 0 4px 0',
  },
  sidePanelTitle: {
    fontFamily: FONT,
    fontWeight: 700,
    fontSize: '20px',
    lineHeight: '28px',
    color: theme.palette.text.primary,
    margin: '0 0 16px 0',
  },
  metricCardsWrapper: {
    display: 'flex',
    flexDirection: 'row' as const,
    flexWrap: 'wrap' as const,
    gap: '8px',
  },
  metricCard: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'flex-start',
    padding: '16px',
    gap: '8px',
    minWidth: '96px',
    flex: 1,
    background: theme.palette.background.paper,
    borderRadius: '4px',
    boxShadow: '0px 1px 3px rgba(0,0,0,0.08)',
    boxSizing: 'border-box' as const,
  },
  metricCardLabel: {
    fontFamily: FONT,
    fontWeight: 400,
    fontSize: '14px',
    lineHeight: '20px',
    color: theme.palette.text.secondary,
    margin: 0,
  },
  metricValueBlue: {
    fontFamily: FONT,
    fontWeight: 700,
    fontSize: '40px',
    lineHeight: '52px',
    letterSpacing: '-0.5px',
    color: '#007EB1',
    margin: 0,
  },
  metricValueGreen: {
    fontFamily: FONT,
    fontWeight: 700,
    fontSize: '40px',
    lineHeight: '52px',
    letterSpacing: '-0.5px',
    color: '#018136',
    margin: 0,
  },
  issueGroupsWrapper: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '16px',
  },
  issueGroup: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '6px',
  },
  issueGroupHeader: {
    display: 'flex',
    flexDirection: 'row' as const,
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  issueGroupLabel: {
    fontFamily: FONT,
    fontWeight: 600,
    fontSize: '14px',
    lineHeight: '20px',
    color: theme.palette.text.primary,
    margin: 0,
  },
  issueGroupCount: {
    fontFamily: FONT,
    fontWeight: 700,
    fontSize: '12px',
    lineHeight: '16px',
    color: theme.palette.text.secondary,
  },
  pipelineStatusDot: {
    width: '10px',
    height: '10px',
    borderRadius: '50%',
    flexShrink: 0,
    display: 'inline-block',
  },
  pipelineRow: {
    display: 'flex',
    flexDirection: 'row' as const,
    alignItems: 'center',
    gap: '8px',
  },
  pipelineLabel: {
    fontFamily: FONT,
    fontWeight: 600,
    fontSize: '13px',
    lineHeight: '18px',
    color: theme.palette.text.primary,
    margin: 0,
    flex: 1,
    whiteSpace: 'nowrap' as const,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  pipelineMeta: {
    fontFamily: FONT,
    fontWeight: 400,
    fontSize: '11px',
    lineHeight: '16px',
    color: theme.palette.text.secondary,
    margin: 0,
  },
  loadingWrapper: {
    display: 'flex',
    justifyContent: 'center',
    padding: '24px 0',
  },
}));

// ── Types ──────────────────────────────────────────────────────────────────────

type CatalogEntity = {
  metadata: {
    name: string;
    annotations?: Record<string, string>;
    namespace?: string;
  };
  kind: string;
  spec?: {
    type?: string;
    owner?: string;
  };
};

type PipelineInfo = {
  status: string;
  ref: string;
  web_url: string;
  id: number;
};

// ── Constants ───────────────────────────────────────────────────────────────────
// GITLAB_INSTANCE is resolved at runtime from the app config so the page
// works regardless of whether GITLAB_HOST is code.siemens.com or any other
// configured host.
const GITLAB_PROJECT_SLUG_ANNOTATION = 'gitlab.com/project-slug';

const PIPELINE_STATUS_COLOR: Record<string, string> = {
  success: '#018136',
  failed: '#D72339',
  running: '#007EB1',
  pending: '#E96401',
  canceled: '#6b7280',
  skipped: '#6b7280',
  created: '#9ca3af',
  manual: '#E96401',
  scheduled: '#9ca3af',
};

const getPipelineColor = (status: string) =>
  PIPELINE_STATUS_COLOR[status] ?? '#9ca3af';

// ── Helpers ───────────────────────────────────────────────────────────────────

const getProjectSlug = (entity: CatalogEntity): string | undefined =>
  entity.metadata.annotations?.[GITLAB_PROJECT_SLUG_ANNOTATION];

const getEntityRef = (entity: CatalogEntity) =>
  `/catalog/default/${entity.kind.toLowerCase()}/${entity.metadata.name}`;

// ── Component ─────────────────────────────────────────────────────────────────

export const ArtifactRepositoryPage = () => {
  const classes = useStyles();
  const catalogApi = useApi(catalogApiRef);
  const gitlabBuilder = useApi(GitlabCIApiRef);
  const configApi = useApi(configApiRef);

  // Read the configured GitLab host at runtime so the page works with any
  // GITLAB_HOST value (code.siemens.com in production, gitlab.tu-ilmenau.de in dev).
  // Note: bracket notation like [0] is not valid in Backstage ConfigReader —
  // use getOptionalConfigArray to navigate array keys.
  const GITLAB_INSTANCE =
    configApi.getOptionalConfigArray('integrations.gitlab')?.[0]?.getOptionalString('host') ??
    'gitlab.tu-ilmenau.de';

  const [entities, setEntities] = useState<CatalogEntity[]>([]);
  const [entitiesLoading, setEntitiesLoading] = useState(true);
  const [pipelines, setPipelines] = useState<Record<string, PipelineInfo | null>>({});
  const [unavailableRepos, setUnavailableRepos] = useState<Set<string>>(new Set());
  const [pipelinesLoading, setPipelinesLoading] = useState(false);

  // ── Fetch entities from catalog ───────────────────────────────────────────
  useEffect(() => {
    let mounted = true;
    setEntitiesLoading(true);

    catalogApi
      .getEntities({
        fields: [
          'metadata.name',
          'metadata.namespace',
          'metadata.annotations',
          'kind',
          'spec.type',
          'spec.owner',
        ],
        filter: [
          { kind: 'Component' },
          { kind: 'API' },
          { kind: 'System' },
        ],
      })
      .then(resp => {
        if (!mounted) return;
        // Keep only entities that belong to the code4ev GitLab group
        const filtered = (resp.items as CatalogEntity[]).filter(e => {
          const slug = getProjectSlug(e);
          return slug !== undefined;
        });
        setEntities(filtered);
      })
      .catch(() => {
        if (mounted) setEntities([]);
      })
      .finally(() => {
        if (mounted) setEntitiesLoading(false);
      });

    return () => { mounted = false; };
  }, [catalogApi]);

  // ── Fetch pipeline status from GitLab for each entity ────────────────────
  useEffect(() => {
    if (entities.length === 0) return;

    const slugEntities = entities.filter(e => !!getProjectSlug(e));
    if (slugEntities.length === 0) return;

    setPipelinesLoading(true);
    const gitlabApi = gitlabBuilder.build(GITLAB_INSTANCE);

    Promise.allSettled(
      slugEntities.map(async entity => {
        const slug = getProjectSlug(entity)!;
        const encodedSlug = encodeURIComponent(slug);
        try {
          const result = await gitlabApi.getPipelineSummary(encodedSlug);
          const latest = result?.[0] ?? null;
          return { name: entity.metadata.name, pipeline: latest as PipelineInfo | null, unavailable: false };
        } catch {
          return { name: entity.metadata.name, pipeline: null, unavailable: true };
        }
      }),
    ).then(results => {
      const map: Record<string, PipelineInfo | null> = {};
      const unavailable = new Set<string>();
      results.forEach(r => {
        if (r.status === 'fulfilled') {
          map[r.value.name] = r.value.pipeline;
          if (r.value.unavailable) unavailable.add(r.value.name);
        }
      });
      setPipelines(map);
      setUnavailableRepos(unavailable);
      setPipelinesLoading(false);
    });
  }, [entities, gitlabBuilder]);

  // ── Derived counts ────────────────────────────────────────────────────────
  const components = entities.filter(e => e.kind === 'Component');
  const apis = entities.filter(e => e.kind === 'API');
  const systems = entities.filter(e => e.kind === 'System');
  const failedPipelines = Object.values(pipelines).filter(p => p?.status === 'failed').length;

  const summaryCards = [
    { label: 'Components', value: String(components.length), color: '#007EB1' },
    { label: 'APIs', value: String(apis.length), color: '#007EB1' },
    { label: 'Systems', value: String(systems.length), color: '#007EB1' },
    { label: 'Failed Pipelines', value: String(failedPipelines), color: '#D72339' },
  ];

  // Show all entities; the list is scrollable
  const recentEntities = entities;

  // Repos with pipeline data for the sidebar (up to 8)
  const pipelineRepos = entities
    .filter(e => !!getProjectSlug(e))
    .slice(0, 8);

  return (
    <Page themeId="home">
      <Header title="Artifact Repository" subtitle="Browse and track all CODE4EV catalog entities" />
      <Content>
        <div className={classes.layout}>
          {/* ── Main content ── */}
          <div className={classes.mainContent}>
            {/* Summary row */}
            <p className={classes.sectionLabel}>Overview</p>
            {entitiesLoading ? (
              <div className={classes.loadingWrapper}><CircularProgress size={28} /></div>
            ) : (
              <Grid container spacing={2} style={{ marginBottom: '24px' }}>
                {summaryCards.map(c => (
                  <Grid item md={3} xs={6} key={c.label} style={{ display: 'flex' }}>
                    <div className={classes.summaryCard} style={{ width: '100%' }}>
                      <p className={classes.summaryCardLabel}>{c.label}</p>
                      <p className={classes.summaryCardValue} style={{ color: c.color }}>{c.value}</p>
                    </div>
                  </Grid>
                ))}
              </Grid>
            )}

            {/* Recent entities */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
              <p className={classes.sectionLabel} style={{ margin: 0 }}>Recent Entities</p>
              <Link to="/catalog/list" className={classes.browseLink}>
                Browse all
                <svg width="16" height="16" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                  <path d="M8 5l5 5-5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </Link>
            </div>

            {entitiesLoading ? (
              <div className={classes.loadingWrapper}><CircularProgress size={28} /></div>
            ) : recentEntities.length === 0 ? (
              <p className={classes.summaryCardLabel} style={{ padding: '16px 0' }}>No entities found in the catalog yet.</p>
            ) : (
              <Grid container spacing={2} style={{ maxHeight: '560px', overflowY: 'auto' }}>
                {recentEntities.map(entity => {
                  const pipeline = pipelines[entity.metadata.name];
                  const isUnavailable = unavailableRepos.has(entity.metadata.name);
                  const pipelineStatus = pipeline?.status ?? 'unknown';
                  const borderColor = isUnavailable ? '#6b7280' : getPipelineColor(pipelineStatus);
                  const owner = entity.spec?.owner ?? '—';
                  const type = entity.spec?.type ?? entity.kind;
                  return (
                    <Grid item xs={12} key={entity.metadata.name}>
                      <Link to={getEntityRef(entity)} underline="none">
                        <div className={classes.entityCard} style={{ borderLeftColor: borderColor }}>
                          <div className={classes.entityCardRow}>
                            <p className={classes.entityCardTitle}>{entity.metadata.name}</p>
                            {isUnavailable ? (
                              <Chip
                                className={classes.chip}
                                label="unavailable"
                                size="small"
                                style={{ background: '#6b7280', color: '#FFFFFF' }}
                              />
                            ) : pipeline ? (
                              <Chip
                                className={classes.chip}
                                label={pipeline.status}
                                size="small"
                                style={{ background: getPipelineColor(pipeline.status), color: '#FFFFFF' }}
                              />
                            ) : (
                              <Chip
                                className={classes.chip}
                                label="no pipeline"
                                size="small"
                                style={{ background: '#9ca3af', color: '#FFFFFF' }}
                              />
                            )}
                          </div>
                          <p className={classes.entityCardSub}>
                            {entity.kind} · {type} · {owner}
                            {pipeline?.ref ? ` · ${pipeline.ref}` : ''}
                          </p>
                        </div>
                      </Link>
                    </Grid>
                  );
                })}
              </Grid>
            )}
          </div>

          {/* ── Divider ── */}
          <div className={classes.divider} />

          {/* ── Side panel ── */}
          <div className={classes.sidePanel}>
            {/* Metric cards */}
            <div>
              <p className={classes.sidePanelLabel}>Tracking</p>
              <h2 className={classes.sidePanelTitle}>Repository Health</h2>
              {entitiesLoading ? (
                <div className={classes.loadingWrapper}><CircularProgress size={24} /></div>
              ) : (
                <div className={classes.metricCardsWrapper}>
                  <div className={classes.metricCard}>
                    <p className={classes.metricCardLabel}>Total repos</p>
                    <p className={classes.metricValueBlue}>{entities.length}</p>
                  </div>
                  <div className={classes.metricCard}>
                    <p className={classes.metricCardLabel}>Passing</p>
                    <p className={classes.metricValueGreen}>
                      {Object.values(pipelines).filter(p => p?.status === 'success').length}
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Pipeline status by repository */}
            <div>
              <p className={classes.sidePanelLabel}>By Repository</p>
              <h3 className={classes.sidePanelTitle}>Pipeline Status</h3>

              {pipelinesLoading || entitiesLoading ? (
                <div className={classes.loadingWrapper}><CircularProgress size={24} /></div>
              ) : pipelineRepos.length === 0 ? (
                <p className={classes.pipelineMeta}>No repositories found.</p>
              ) : (
                <div className={classes.issueGroupsWrapper}>
                  {pipelineRepos.map(entity => {
                    const pipeline = pipelines[entity.metadata.name];
                    const isUnavailable = unavailableRepos.has(entity.metadata.name);
                    const status = isUnavailable ? 'unavailable' : (pipeline?.status ?? 'unknown');
                    const color = isUnavailable ? '#6b7280' : getPipelineColor(status);
                    const slug = getProjectSlug(entity)!;
                    const pipelineUrl = pipeline?.web_url
                      ?? `https://${GITLAB_INSTANCE}/${slug}/-/pipelines`;
                    return (
                      <div key={entity.metadata.name} className={classes.issueGroup}>
                        <div className={classes.pipelineRow}>
                          <Tooltip title={status} placement="left">
                            <span
                              className={classes.pipelineStatusDot}
                              style={{ background: color }}
                            />
                          </Tooltip>
                          <p className={classes.pipelineLabel}>{entity.metadata.name}</p>
                          <a
                            href={pipelineUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color, fontSize: '11px', fontFamily: FONT, fontWeight: 600, whiteSpace: 'nowrap' }}
                          >
                            {status}
                          </a>
                        </div>
                        {pipeline?.ref && (
                          <p className={classes.pipelineMeta} style={{ paddingLeft: '18px' }}>
                            branch: {pipeline.ref}
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </Content>
    </Page>
  );
};
